import React from 'react';
import { AwardIcon, CompassIcon, HeartHandshakeIcon, MountainIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { SectionHeading } from '../components/SectionHeading';
const team = [{
  name: 'Nima Sherpa',
  role: 'Mountain Operations Lead',
  image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=900&q=85',
  bio: 'Born in Solukhumbu, Nima has spent two decades guiding and shaping careful journeys through the Himalaya.'
}, {
  name: 'Sushma Karki',
  role: 'Travel Designer',
  image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=900&q=85',
  bio: 'Sushma listens first, then builds itineraries around the people, pace, and places that matter to each traveller.'
}, {
  name: 'Rajan Gurung',
  role: 'Community Partnerships',
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=85',
  bio: 'Rajan works with the hosts, porters, and local teams who make every route feel generous and grounded.'
}];
const reasons = [{
  icon: MountainIcon,
  title: 'Local knowledge, shared openly',
  text: 'Our routes are shaped by people who live close to the trails, seasons, and communities you will meet.'
}, {
  icon: ShieldCheckIcon,
  title: 'Thoughtful from first call to final step',
  text: 'Clear preparation, small groups, and measured pacing help leave room for confidence as well as discovery.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Travel with reciprocity',
  text: 'We choose local stays, work with local teams, and keep our groups mindful of the places that welcome us.'
}];
export function AboutUs() {
  return <div className="min-h-screen w-full bg-[#f8f5ed] text-[#17362e]" data-id="element-272">
      <Header data-id="element-273" />
      <main data-id="element-274">
        <section className="bg-[#17362e] px-5 py-16 text-white sm:px-8 lg:px-12 lg:py-24" data-id="element-275">
          <div className="mx-auto grid max-w-[1180px] gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-end" data-id="element-276">
            <div data-id="element-277">
              <p className="mb-5 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.27em] text-[#c8dfbd]" data-id="element-278">
                <CompassIcon className="h-4 w-4" aria-hidden="true" data-id="element-279" />
                About Tour in Nepal
              </p>
              <h1 className="max-w-3xl font-serif text-5xl leading-[0.98] sm:text-6xl lg:text-7xl" data-id="element-280">
                Journeys made with the mountain, not just toward it.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-white/75" data-id="element-281">
                We are a Nepal-rooted travel team creating considered treks,
                cultural journeys, and private escapes for people who want to
                travel with curiosity and care.
              </p>
            </div>
            <figure className="overflow-hidden border border-white/15" data-id="element-282">
              <img src="https://images.unsplash.com/photo-1583395838144-4c5d6b4f550d?auto=format&fit=crop&w=1200&q=85" alt="Himalayan foothills beneath soft morning cloud" className="h-72 w-full object-cover sm:h-96" data-id="element-283" />
            </figure>
          </div>
        </section>

        <section className="px-5 py-20 sm:px-8 sm:py-28 lg:px-12" data-id="element-284">
          <div className="mx-auto grid max-w-[1180px] gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24" data-id="element-285">
            <SectionHeading eyebrow="Our overview" title="Travel should leave a place more visible, not more distant." data-id="element-286" />
            <div className="space-y-6 text-lg leading-8 text-[#52635a]" data-id="element-287">
              <p data-id="element-288">
                Tour in Nepal began with a simple belief: a great journey is
                built through relationships. The relationship between a guide
                and a guest. Between a village and the travellers passing
                through. Between a plan on paper and the weather, energy, and
                unexpected invitations that make a trip feel alive.
              </p>
              <p data-id="element-289">
                Our team combines long trail experience with an editor&apos;s
                eye for detail. We make room for early starts, warm meals, quiet
                monasteries, and the practical support that lets you pay
                attention to all of it. Whether you are coming for a first
                Himalayan trek or returning for a deeper look at Nepal, we help
                you find a route with real character.
              </p>
              <div className="grid gap-4 border-t border-[#cfd3c8] pt-7 sm:grid-cols-2" data-id="element-290">
                <div data-id="element-291">
                  <p className="font-serif text-4xl text-[#17362e]" data-id="element-292">12+</p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#718074]" data-id="element-293">
                    Years on the trail
                  </p>
                </div>
                <div data-id="element-294">
                  <p className="font-serif text-4xl text-[#17362e]" data-id="element-295">100%</p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#718074]" data-id="element-296">
                    Locally led journeys
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#e7e7db] px-5 py-20 sm:px-8 sm:py-28 lg:px-12" data-id="element-297">
          <div className="mx-auto max-w-[1180px]" data-id="element-298">
            <SectionHeading eyebrow="Why choose us" title="The care behind every mile" data-id="element-299" />
            <div className="mt-12 grid gap-px overflow-hidden border border-[#cbd0c2] bg-[#cbd0c2] md:grid-cols-3" data-id="element-300">
              {reasons.map(({
              icon: Icon,
              title,
              text
            }) => <article key={title} className="bg-[#f8f5ed] p-7 lg:p-9" data-id="element-301">
                  <Icon className="h-7 w-7 text-[#4f8057]" aria-hidden="true" data-id="element-302" />
                  <h2 className="mt-7 font-serif text-3xl leading-tight text-[#17362e]" data-id="element-303">
                    {title}
                  </h2>
                  <p className="mt-4 leading-7 text-[#52635a]" data-id="element-304">{text}</p>
                </article>)}
            </div>
            <div className="mt-10 grid gap-5 border-t border-[#c9cec2] pt-8 sm:grid-cols-2" data-id="element-305">
              <p className="flex gap-3 leading-7 text-[#52635a]" data-id="element-306">
                <AwardIcon className="mt-1 h-5 w-5 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-307" />
                Experienced mountain leaders and carefully matched local teams.
              </p>
              <p className="flex gap-3 leading-7 text-[#52635a]" data-id="element-308">
                <UsersIcon className="mt-1 h-5 w-5 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-309" />
                Small-group decisions that keep the journey personal and
                flexible.
              </p>
            </div>
          </div>
        </section>

        <section className="px-5 py-20 sm:px-8 sm:py-28 lg:px-12" data-id="element-310">
          <div className="mx-auto max-w-[1180px]" data-id="element-311">
            <SectionHeading eyebrow="Our team" title="The people who make the route feel like home" data-id="element-312" />
            <div className="mt-12 grid gap-7 md:grid-cols-3" data-id="element-313">
              {team.map(member => <article key={member.name} className="border-b border-[#cbd0c2] pb-7" data-id="element-314">
                  <img src={member.image} alt={`Portrait of ${member.name}`} className="h-80 w-full object-cover grayscale-[12%]" data-id="element-315" />
                  <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4f8057]" data-id="element-316">
                    {member.role}
                  </p>
                  <h2 className="mt-2 font-serif text-3xl text-[#17362e]" data-id="element-317">
                    {member.name}
                  </h2>
                  <p className="mt-3 leading-7 text-[#52635a]" data-id="element-318">{member.bio}</p>
                </article>)}
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-319" />
    </div>;
}